interface PatientChatHydrationGate {
  isModalOpen: boolean;
  hasSession: boolean;
  isFetching: boolean;
  hasPendingWrite: boolean;
  isHydrated: boolean;
}

/**
 * The query cache still holds the session from the last time the modal was open,
 * and the draft saved on close is newer than it, so hydration waits for the reopen
 * fetch instead of seeding from that copy. It also waits for this tab's own session
 * writes to settle (the close-time save may still be in flight), so the version the
 * write comes back with is known before the fetched session is judged against it.
 * Should the fetch fail, the cached session is still applied once fetching stops.
 */
export function shouldHydratePatientChatSession({
  isModalOpen,
  hasSession,
  isFetching,
  hasPendingWrite,
  isHydrated,
}: PatientChatHydrationGate): boolean {
  return isModalOpen && hasSession && !isFetching && !hasPendingWrite && !isHydrated;
}

interface PatientChatDraftResolution {
  serverDraftPrompt: string | null | undefined;
  /** Version of the fetched session. Bumped by the server on every write. */
  serverVersion: number | null | undefined;
  /** Version this tab's latest confirmed draft save came back with, if any. */
  lastWrittenVersion: number | null;
  localDraftPrompt: string;
  draftPromptAtOpen: string;
}

/**
 * Decides which draft the panel shows once the session arrives.
 *
 * The server draft wins by default: the server may legitimately change it behind
 * this tab's back (a failed or cancelled request puts the sent message back, another
 * tab may have saved). Two cases are newer than the response and keep the local
 * draft, flagged for write-back: the fetch was answered before this tab's last save
 * landed (its version is older than the one that save returned), or something was
 * typed while the fetch was running.
 */
export function resolvePatientChatDraftOnHydrate({
  serverDraftPrompt,
  serverVersion,
  lastWrittenVersion,
  localDraftPrompt,
  draftPromptAtOpen,
}: PatientChatDraftResolution): { draftPrompt: string; needsWriteBack: boolean } {
  const serverDraft = serverDraftPrompt || "";
  const isServerBehindOurWrites =
    lastWrittenVersion !== null &&
    typeof serverVersion === "number" &&
    serverVersion < lastWrittenVersion;
  const keepLocalDraft = isServerBehindOurWrites || localDraftPrompt !== draftPromptAtOpen;
  const draftPrompt = keepLocalDraft ? localDraftPrompt : serverDraft;
  return { draftPrompt, needsWriteBack: draftPrompt !== serverDraft };
}
